import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './UserManagement.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));


const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [countryInfo, setCountryInfo] = useState({
    name: '',
    currency: 'USD'
  });

  // Fetch users from the same country as admin
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      console.log('Fetching users...');
      
      // First, try to get admin profile to check country
      const adminResponse = await fetch(`${API_BASE}/api/admin/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!adminResponse.ok) {
        throw new Error('Failed to fetch admin profile');
      }
      
      const adminData = await adminResponse.json();
      
      if (!adminData.success || !adminData.admin) {
        throw new Error('Admin data not found');
      }
      
      const adminProfile = adminData.admin;
      console.log('Admin profile:', adminProfile);
      
      // Store country info for display
      if (adminProfile.country_name || adminProfile.currency_code) {
        setCountryInfo({
          name: adminProfile.country_name || 'Unknown',
          currency: adminProfile.currency_code || 'USD'
        });
      }
      
      // Fetch users - using the updated backend endpoint
      const usersResponse = await fetch(`${API_BASE}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await usersResponse.json();
      
      if (!usersResponse.ok) {
        // Check if it's a database schema error
        if (data.error && data.error.includes('database schema')) {
          throw new Error('Database configuration needed. Please contact administrator.');
        }
        throw new Error(data.error || 'Failed to fetch users');
      }
      
      console.log('Users response:', data);
      
      if (data.success) {
        const usersList = data.users || [];
        setUsers(usersList);
        setFilteredUsers(usersList);
        setTotalUsers(data.total || usersList.length);
        
        // Show success message with country info
        if (data.message) {
          console.log(data.message);
        }
      } else {
        setError(data.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, users]);

  // Calculate pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'LRD': 'L$',
      'XOF': 'CFA',
      'EUR': '€',
      'GBP': '£'
    };
    const symbol = symbols[currency] || currency;
    const num = parseFloat(amount) || 0;
    return `${symbol}${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="user-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h2><i className="fas fa-users"></i> User Management</h2>
        <div className="header-info">
          {countryInfo.name && (
            <div className="country-info">
              <i className="fas fa-flag"></i>
              <span>Country: {countryInfo.name} ({countryInfo.currency})</span>
            </div>
          )}
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-icon total">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-info">
              <h3>{totalUsers}</h3>
              <p>Total Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon active">
              <i className="fas fa-user-check"></i>
            </div>
            <div className="stat-info">
              <h3>{users.filter(u => u.is_active).length}</h3>
              <p>Active Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon inactive">
              <i className="fas fa-user-times"></i>
            </div>
            <div className="stat-info">
              <h3>{users.filter(u => !u.is_active).length}</h3>
              <p>Inactive Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="user-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search users by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="refresh-button">
          <button onClick={fetchUsers}>
            <i className="fas fa-sync-alt"></i>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <div>
            <strong>Error:</strong> {error}
            <div className="error-hint">
              <small>
                {error.includes('Database configuration') && 
                  'Please run database migrations or contact administrator.'}
                {error.includes('country') && 
                  'Your admin account may not be assigned to a country.'}
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Registration Date</th>
              <th>Last Login</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map(user => (
                <tr 
                  key={user.id} 
                  onClick={() => setSelectedUser(user)}
                  className={`${selectedUser?.id === user.id ? 'selected' : ''} ${!user.is_active ? 'inactive-user' : ''}`}
                >
                  <td className="user-id">{user.id}</td>
                  <td className="user-username">
                    <div className="username-cell">
                      <span className="username">{user.username}</span>
                      {user.is_active === false && (
                        <span className="inactive-tag">Inactive</span>
                      )}
                    </div>
                  </td>
                  <td className="user-email">{user.email}</td>
                  <td className="user-phone">{user.phone || 'N/A'}</td>
                  <td>
                    {user.is_active ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td className="user-date">{formatDate(user.created_at)}</td>
                  <td className="user-date">
                    {user.last_login ? formatDate(user.last_login) : 'Never'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-users">
                  <div className="no-users-content">
                    <i className="fas fa-user-slash"></i>
                    <div>
                      <p>No users found</p>
                      <small>
                        {searchTerm ? 'Try a different search term' : 
                         countryInfo.name ? `No users in ${countryInfo.name}` : 
                         'No users available or database not configured'}
                      </small>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredUsers.length > usersPerPage && (
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-btn"
          >
            <i className="fas fa-chevron-left"></i> Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => 
                page === 1 || 
                page === totalPages || 
                (page >= currentPage - 1 && page <= currentPage + 1)
              )
              .map((page, index, array) => {
                // Add ellipsis for gaps
                if (index > 0 && page - array[index - 1] > 1) {
                  return (
                    <React.Fragment key={`ellipsis-${page}`}>
                      <span className="ellipsis">...</span>
                      <button
                        onClick={() => paginate(page)}
                        className={`page-number ${currentPage === page ? 'active' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    key={page}
                    onClick={() => paginate(page)}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
          </div>
          
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>User Details</h3>
              <button className="close-btn" onClick={() => setSelectedUser(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="user-profile-section">
                <div className="profile-header">
                  <div className="avatar">
                    {selectedUser.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="profile-info">
                    <h4>{selectedUser.username}</h4>
                    <p className="user-email">{selectedUser.email}</p>
                    {selectedUser.is_active ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </div>
                </div>
                
                <div className="user-details-grid">
                  <div className="detail-item">
                    <label><i className="fas fa-id-card"></i> User ID</label>
                    <span>{selectedUser.id}</span>
                  </div>
                  <div className="detail-item">
                    <label><i className="fas fa-phone"></i> Phone</label>
                    <span>{selectedUser.phone || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label><i className="fas fa-calendar"></i> Registered</label>
                    <span>{formatDate(selectedUser.created_at)}</span>
                  </div>
                  <div className="detail-item">
                    <label><i className="fas fa-sign-in-alt"></i> Last Login</label>
                    <span>{selectedUser.last_login ? formatDate(selectedUser.last_login) : 'Never'}</span>
                  </div>
                  {selectedUser.country_name && (
                    <div className="detail-item">
                      <label><i className="fas fa-globe"></i> Country</label>
                      <span>{selectedUser.country_name}</span>
                    </div>
                  )}
                  {selectedUser.local_currency && (
                    <div className="detail-item">
                      <label><i className="fas fa-money-bill"></i> Local Currency</label>
                      <span>{selectedUser.local_currency}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="modal-actions">
                <button className="close-modal-btn" onClick={() => setSelectedUser(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;